import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { TrendDataPoint } from '../../types/analytics';
import { useViewport } from '../../hooks/useViewport';

interface SentimentLineChartProps {
  data: TrendDataPoint[];
  height?: number;
  showMovingAverage?: boolean;
}

const SentimentLineChart: React.FC<SentimentLineChartProps> = ({
  data,
  height = 300,
  showMovingAverage = false,
}) => {
  const { isMobile, isTablet } = useViewport();

  // Prepare data with moving average
  const chartData = data.map((item) => ({
    ...item,
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    movingAvg: showMovingAverage ? item.moving_average ?? null : null,
  }));

  // Responsive values
  const fontSize = isMobile ? 10 : 12;
  const responsiveHeight = isMobile ? 250 : height;
  const margins = isMobile
    ? { top: 5, right: 10, left: 0, bottom: 5 }
    : isTablet
    ? { top: 5, right: 20, left: 10, bottom: 5 }
    : { top: 5, right: 30, left: 20, bottom: 5 };
  const dotRadius = isMobile ? 2 : 4;
  const activeDotRadius = isMobile ? 4 : 6;

  return (
    <ResponsiveContainer width="100%" height={responsiveHeight}>
      <LineChart data={chartData} margin={margins}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="date"
          tick={{ fontSize, fontFamily: 'Open Sans', fill: '#767472' }}
          stroke="#e5e7eb"
          interval={isMobile ? 'preserveStartEnd' : 'preserveEnd'}
        />
        <YAxis
          domain={[-1, 1]}
          tick={{ fontSize, fontFamily: 'Open Sans', fill: '#767472' }}
          stroke="#e5e7eb"
          width={isMobile ? 30 : 60}
          label={isMobile ? undefined : {
            value: 'Sentiment Score',
            angle: -90,
            position: 'insideLeft',
            style: { fontSize, fontFamily: 'Open Sans', fill: '#767472' },
          }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontFamily: 'Open Sans',
            fontSize,
          }}
          formatter={(value: number) => [value.toFixed(3), 'Sentiment']}
        />
        <Line
          type="monotone"
          dataKey="avg_sentiment_score"
          stroke="#c6a664"
          strokeWidth={2}
          dot={{ fill: '#c6a664', r: dotRadius }}
          activeDot={{ r: activeDotRadius }}
          name="Sentiment Score"
        />
        {showMovingAverage && (
          <Line
            type="monotone"
            dataKey="movingAvg"
            stroke="#1f1f1f"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            name="Moving Average"
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default SentimentLineChart;
